import React, { useEffect, createElement } from 'react';
import { SEO } from '../components/SEO';
import { breadcrumbSchema } from '../utils/structuredData';
import { ArrowLeftIcon, CheckCircleIcon, ClockIcon, VideoIcon } from 'lucide-react';
export function BookingPage() {
  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  const breadcrumbs = [{
    name: 'Home',
    url: 'https://close-crm-agentur.de'
  }, {
    name: 'Strategiegespräch',
    url: 'https://close-crm-agentur.de/Kostenloses-Strategiegespraech'
  }];
  const structuredData = [breadcrumbSchema(breadcrumbs)];
  return <>
      <SEO title="Kostenloses Strategiegespräch buchen | Close CRM Beratung" description="Buchen Sie jetzt Ihr 30-minütiges kostenloses Strategiegespräch. Wir analysieren Ihre Vertriebssituation und zeigen konkrete Potenziale mit Close CRM auf. ✓ 30 Min ✓ Video-Call ✓ 100% Kostenlos" canonical="https://close-crm-agentur.de/Kostenloses-Strategiegespraech" structuredData={structuredData} data-id="element-342" />
      <div className="min-h-screen bg-[#0a0a0a]" data-id="element-343">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-lg border-b border-white/5" data-id="element-344">
          <nav className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8" data-id="element-345">
            <div className="flex items-center justify-center sm:justify-between h-14 sm:h-16 lg:h-20 gap-2 sm:gap-4" data-id="element-346">
              <a href="/" className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0" aria-label="Close CRM Agentur Homepage" data-id="element-347">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#0ea5e9] flex items-center justify-center flex-shrink-0" data-id="element-348">
                  <span className="text-white font-bold text-xs sm:text-sm" data-id="element-349">
                    C
                  </span>
                </div>
                <span className="text-white font-bold text-sm sm:text-base lg:text-lg whitespace-nowrap" data-id="element-350">
                  Close CRM Agentur
                </span>
              </a>
              <a href="/" className="hidden sm:flex items-center justify-center gap-2 text-gray-400 hover:text-white transition-colors text-sm flex-shrink-0" data-id="element-351">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-352" />
                Zurück zur Startseite
              </a>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <main className="pt-24 pb-16" data-id="element-353">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-354">
            {/* Intro Section */}
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-355">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" data-id="element-356">
                Kostenloses Strategiegespräch buchen
              </h1>
              <p className="text-lg text-gray-400 mb-8" data-id="element-357">
                Buchen Sie jetzt Ihr 30-minütiges Strategiegespräch. Wir
                analysieren Ihre aktuelle Vertriebssituation und zeigen Ihnen
                konkrete Potenziale mit Close CRM auf.
              </p>

              {/* Benefits */}
              <div className="grid sm:grid-cols-3 gap-6 mb-12" data-id="element-358">
                <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#1a1a1a] border border-white/5" data-id="element-359">
                  <div className="w-12 h-12 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center" data-id="element-360">
                    <ClockIcon className="w-6 h-6 text-[#0ea5e9]" data-id="element-361" />
                  </div>
                  <span className="text-white font-medium" data-id="element-362">30 Minuten</span>
                  <span className="text-gray-500 text-sm" data-id="element-363">
                    Fokussiertes Gespräch
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#1a1a1a] border border-white/5" data-id="element-364">
                  <div className="w-12 h-12 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center" data-id="element-365">
                    <VideoIcon className="w-6 h-6 text-[#0ea5e9]" data-id="element-366" />
                  </div>
                  <span className="text-white font-medium" data-id="element-367">Video-Call</span>
                  <span className="text-gray-500 text-sm" data-id="element-368">
                    Zoom oder Google Meet
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#1a1a1a] border border-white/5" data-id="element-369">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center" data-id="element-370">
                    <CheckCircleIcon className="w-6 h-6 text-emerald-400" data-id="element-371" />
                  </div>
                  <span className="text-white font-medium" data-id="element-372">100% Kostenlos</span>
                  <span className="text-gray-500 text-sm" data-id="element-373">
                    Keine Verpflichtung
                  </span>
                </div>
              </div>

              {/* What to expect */}
              <div className="text-left p-6 rounded-2xl bg-[#1a1a1a] border border-white/5 mb-12" data-id="element-374">
                <h2 className="text-xl font-bold text-white mb-4" data-id="element-375">
                  Was Sie erwarten können:
                </h2>
                <ul className="space-y-3" data-id="element-376">
                  {['Analyse Ihrer aktuellen Vertriebsprozesse', 'Identifikation von Optimierungspotenzialen', 'Konkrete Empfehlungen für Close CRM', 'Antworten auf all Ihre Fragen', 'Unverbindliches Angebot (falls gewünscht)'].map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-377">
                      <CheckCircleIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-378" />
                      <span className="text-gray-300" data-id="element-379">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            {/* Calendly Widget */}
            <div className="max-w-4xl mx-auto" data-id="element-380">
              <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#1a1a1a]" data-id="element-381">
                <div className="calendly-inline-widget" data-url="https://calendly.com/justus-kraft/crm-automatisierung-demo-website?hide_gdpr_banner=1&primary_color=0ea5e9" style={{
                minWidth: '320px',
                height: '700px'
              }} data-id="element-382" />
              </div>
              <p className="text-center text-sm text-gray-500 mt-6" data-id="element-383">
                Kein passender Termin dabei?{' '}
                <a href="mailto:justus@crm-kraft.de" className="text-[#0ea5e9] hover:underline" data-id="element-384">
                  Kontaktieren Sie uns direkt
                </a>
              </p>
            </div>

            {/* Trust signals */}
            <div className="max-w-3xl mx-auto mt-16 p-8 rounded-2xl bg-gradient-to-r from-[#0ea5e9]/10 to-[#06b6d4]/10 border border-[#0ea5e9]/20 text-center" data-id="element-385">
              <p className="text-lg text-white font-medium mb-2" data-id="element-386">
                Über 110 Unternehmer vertrauen uns bereits
              </p>
              <p className="text-gray-400" data-id="element-387">
                Durchschnittlich erzielen unsere Kunden 3.2x ROI innerhalb von 6
                Monaten
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-[#0a0a0a] border-t border-white/5 py-8" data-id="element-388">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-389">
            <p className="text-gray-600 text-sm" data-id="element-390">
              © {new Date().getFullYear()} Close CRM Agentur. Alle Rechte
              vorbehalten.
            </p>
          </div>
        </footer>
      </div>
    </>;
}